/**
 * useRealtimeMessages Hook
 * Manages real-time message subscriptions for a booking chat
 */

import { useState, useEffect, useRef, useCallback } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'

export function useRealtimeMessages(bookingId, threadId = null) {
  const [messages, setMessages] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [isConnected, setIsConnected] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const channelRef = useRef(null)
  const { user } = useAuth()

  // Fetch initial messages
  const fetchMessages = useCallback(async (limit = 50, offset = 0) => {
    if (!bookingId || !threadId) {
      setMessages([])
      setHasMore(false)
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setError(null)

      let messagesQuery = supabase
        .from('booking_messages')
        .select(`
          *,
          sender:users!sender_id(id, full_name, avatar_url)
        `, { count: 'exact' })
        .eq('booking_id', bookingId)
        .is('deleted_at', null)
        .order('created_at', { ascending: true })
        .range(offset, offset + limit - 1)

      messagesQuery = messagesQuery.eq('thread_id', threadId)

      const { data, error: fetchError, count } = await messagesQuery

      if (fetchError) throw fetchError

      setMessages(data || [])
      setHasMore(count > offset + limit)
    } catch (err) {
      console.error('Error fetching messages:', err)
      setError(err.message)
    } finally {
      setIsLoading(false)
    }
  }, [bookingId, threadId])

  // Send a message
  const sendMessage = useCallback(async (messageText, parentMessageId = null) => {
    if (!bookingId || !threadId || !messageText || !messageText.trim()) {
      console.warn('❌ Cannot send message: Missing conversation, booking ID, or message text')
      return { success: false, error: 'Please select a conversation before sending a message.' }
    }

    try {
      console.log(`📤 Sending message to booking ${bookingId}...`)

      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        console.error('❌ Not authenticated - no session found')
        return { success: false, error: 'Not authenticated. Please log in again.' }
      }

      const response = await fetch(`/api/messages/bookings/${bookingId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          message_text: messageText,
          thread_id: threadId,
          parent_message_id: parentMessageId
        })
      })

      const result = await response.json()

      if (!response.ok) {
        console.error(`❌ Server error (${response.status}):`, result)

        // Provide user-friendly error messages
        let userMessage = result.message || 'Failed to send message'

        if (result.error === 'CHAT_NOT_YET_AVAILABLE') {
          userMessage = `Chat will open ${result.daysUntil} day(s) before your event`
        } else if (result.error === 'CHAT_EXPIRED') {
          userMessage = 'Chat is no longer available for this booking'
        } else if (result.error === 'FORBIDDEN') {
          userMessage = 'You do not have permission to send messages in this chat'
        } else if (response.status === 401) {
          userMessage = 'Your session has expired. Please log in again.'
        }

        throw new Error(userMessage)
      }

      console.log('✅ Message sent successfully')
      // Message will be added via real-time subscription
      return { success: true, message: result.message }
    } catch (err) {
      console.error('❌ Error sending message:', err)
      return { success: false, error: err.message || 'Failed to send message. Please try again.' }
    }
  }, [bookingId, threadId])

  // Mark message as read
  const markAsRead = useCallback(async (messageId) => {
    if (!messageId) return

    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) return

      const response = await fetch(`/api/messages/${messageId}/read`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        }
      })

      if (!response.ok) {
        const result = await response.json()
        throw new Error(result.message || 'Failed to mark message as read')
      }

      return { success: true }
    } catch (err) {
      console.error('Error marking message as read:', err)
      return { success: false, error: err.message }
    }
  }, [])

  // Mark all unread messages as read (uses bulk endpoint)
  const markAllAsRead = useCallback(async () => {
    console.log('📖 markAllAsRead called', { user: !!user, bookingId })

    if (!user || !bookingId) {
      console.warn('📖 markAllAsRead: Missing user or bookingId', { user: !!user, bookingId })
      return { success: false, error: 'Missing user or bookingId' }
    }

    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (!session) {
        console.warn('📖 markAllAsRead: No session')
        return { success: false, error: 'Not authenticated' }
      }

      console.log('📖 markAllAsRead: Calling API for booking', bookingId)

      const response = await fetch(`/api/messages/bookings/${bookingId}/mark-all-read`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session.access_token}`
        },
        body: JSON.stringify({
          thread_id: threadId
        })
      })

      const result = await response.json()
      console.log('📖 markAllAsRead: API response', { ok: response.ok, result })

      if (!response.ok) {
        throw new Error(result.message || 'Failed to mark messages as read')
      }

      // Update local state to reflect read status. Return the SAME array when nothing
      // changed: a fresh array on every call re-ran the conversation view's mark-as-read
      // effect, which called this again, every ~2 seconds, forever (Render logs, 9 Sep 2026).
      setMessages(prev => {
        let changed = false
        const next = prev.map(msg => {
          if (msg.sender_id === user.id) return msg // Don't change own messages
          if (msg.is_read_customer && msg.is_read_photographer) return msg
          changed = true
          return {
            ...msg,
            is_read_customer: true,
            is_read_photographer: true
          }
        })
        return changed ? next : prev
      })

      return { success: true }
    } catch (err) {
      console.error('📖 markAllAsRead: Error', err)
      return { success: false, error: err.message }
    }
  }, [bookingId, threadId, user])

  // Set up real-time subscription
  useEffect(() => {
    setMessages([])
    setIsConnected(false)

    if (!bookingId || !threadId) {
      setIsLoading(false)
      return
    }

    // Fetch initial messages
    fetchMessages()

    // Set up real-time subscription
    const channel = supabase
      .channel(`booking_messages:${bookingId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'booking_messages',
          filter: `booking_id=eq.${bookingId}`
        },
        async (payload) => {
          if (payload.new.thread_id !== threadId) return

          // Fetch sender info for the new message
          const { data: sender } = await supabase
            .from('users')
            .select('id, full_name, avatar_url')
            .eq('id', payload.new.sender_id)
            .single()

          const newMessage = {
            ...payload.new,
            sender
          }

          setMessages(prev => {
            // Avoid duplicates
            if (prev.some(msg => msg.id === newMessage.id)) {
              return prev
            }
            return [...prev, newMessage]
          })
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'booking_messages',
          filter: `booking_id=eq.${bookingId}`
        },
        (payload) => {
          if (payload.new.thread_id !== threadId) return

          // Deleted by staff (hidden, deleted_at set): it leaves the open chat right away
          if (payload.new.deleted_at) {
            setMessages(prev => prev.filter(msg => msg.id !== payload.new.id))
            return
          }

          // Update message (e.g., read status changed)
          setMessages(prev =>
            prev.map(msg =>
              msg.id === payload.new.id
                ? { ...msg, ...payload.new }
                : msg
            )
          )
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'DELETE',
          schema: 'public',
          table: 'booking_messages',
          filter: `booking_id=eq.${bookingId}`
        },
        (payload) => {
          if (payload.old.thread_id !== threadId) return

          // Remove deleted message
          setMessages(prev => prev.filter(msg => msg.id !== payload.old.id))
        }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          setIsConnected(true)
          console.log(`✅ Subscribed to chat for booking ${bookingId}`)
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          setIsConnected(false)
          console.error(`❌ Subscription error for booking ${bookingId}:`, status)
        }
      })

    channelRef.current = channel

    // Cleanup on unmount
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current)
        console.log(`🔌 Unsubscribed from chat for booking ${bookingId}`)
      }
    }
  }, [bookingId, threadId, fetchMessages])

  return {
    messages,
    isLoading,
    error,
    isConnected,
    hasMore,
    sendMessage,
    markAsRead,
    markAllAsRead,
    refetch: fetchMessages
  }
}
